import React, { useEffect, useState } from 'react';
import { Navigation } from './components/layout/Navigation';
import { Footer } from './components/layout/Footer';
import { HomePage } from './pages/HomePage';
import { ProductsPage } from './pages/ProductsPage';
import { SolutionsPage } from './pages/SolutionsPage';
import { DevelopersPage } from './pages/DevelopersPage';
import { DocsHomePage } from './pages/docs/DocsHomePage';
import { GettingStartedPage } from './pages/docs/GettingStartedPage';
import { AuthenticationGuidePage } from './pages/docs/AuthenticationGuidePage';
import { IdempotencyGuidePage } from './pages/docs/IdempotencyGuidePage';
import { PaginationGuidePage } from './pages/docs/PaginationGuidePage';
import { ErrorsGuidePage } from './pages/docs/ErrorsGuidePage';
import { WebhooksGuidePage } from './pages/docs/WebhooksGuidePage';
import { APIReferenceOverviewPage } from './pages/docs/APIReferenceOverviewPage';
import { APIConventionsPage } from './pages/docs/APIConventionsPage';
import { OrchestrateAPIPage } from './pages/docs/OrchestrateAPIPage';
import { PaymentsAPIPage } from './pages/docs/PaymentsAPIPage';
import { WalletsAPIPage } from './pages/docs/WalletsAPIPage';
import { Mode } from './types';
type PageType = 'home' | 'products' | 'solutions' | 'developers' | 'docs';
export function App() {
  const [mode, setMode] = useState<Mode>('business');
  const [currentPage, setCurrentPage] = useState<PageType>('home');
  const [docsPath, setDocsPath] = useState<string>('');
  // Simple routing logic
  useEffect(() => {
    const handleNavigation = () => {
      const hash = window.location.hash.slice(1); // Remove #
      if (hash === 'products' || hash.startsWith('products/')) {
        setCurrentPage('products');
        setMode('business');
        // If linking to a specific section like #products/orchestrate
        if (hash.startsWith('products/')) {
          const sectionId = hash.replace('products/', '');
          setTimeout(() => {
            document.getElementById(sectionId)?.scrollIntoView({
              behavior: 'smooth'
            });
          }, 100);
        }
      } else if (hash === 'solutions' || hash.startsWith('solutions/')) {
        setCurrentPage('solutions');
        setMode('business');
        // If linking to a specific section like #solutions/fintech-psp
        if (hash.startsWith('solutions/')) {
          const sectionId = hash.replace('solutions/', '');
          setTimeout(() => {
            document.getElementById(sectionId)?.scrollIntoView({
              behavior: 'smooth'
            });
          }, 100);
        }
      } else if (hash === 'developers') {
        setCurrentPage('developers');
        setMode('business');
      } else if (hash.startsWith('/docs')) {
        setCurrentPage('docs');
        setDocsPath(hash);
        setMode('business');
      } else {
        setCurrentPage('home');
      }
    };
    // Initial check
    handleNavigation();
    // Listen for hash changes
    window.addEventListener('hashchange', handleNavigation);
    return () => window.removeEventListener('hashchange', handleNavigation);
  }, []);
  // Handle mode toggle - if switching to individual, go home
  const handleModeChange = (newMode: Mode) => {
    setMode(newMode);
    if (newMode === 'individual' && (currentPage === 'products' || currentPage === 'solutions' || currentPage === 'developers' || currentPage === 'docs')) {
      window.location.hash = ''; // Go home
    }
  };
  const renderDocsPage = () => {
    // Docs routing
    if (docsPath === '/docs' || docsPath === '/docs/') {
      return <DocsHomePage data-id="element-0" />;
    } else if (docsPath === '/docs/guides/getting-started') {
      return <GettingStartedPage data-id="element-1" />;
    } else if (docsPath === '/docs/guides/authentication') {
      return <AuthenticationGuidePage data-id="element-2" />;
    } else if (docsPath === '/docs/guides/idempotency') {
      return <IdempotencyGuidePage data-id="element-3" />;
    } else if (docsPath === '/docs/guides/pagination') {
      return <PaginationGuidePage data-id="element-4" />;
    } else if (docsPath === '/docs/guides/errors') {
      return <ErrorsGuidePage data-id="element-5" />;
    } else if (docsPath === '/docs/guides/webhooks') {
      return <WebhooksGuidePage data-id="element-6" />;
    } else if (docsPath === '/docs/api-reference' || docsPath === '/docs/api-reference/') {
      return <APIReferenceOverviewPage data-id="element-7" />;
    } else if (docsPath === '/docs/api-conventions') {
      return <APIConventionsPage data-id="element-8" />;
    } else if (docsPath === '/docs/api-reference/orchestrate') {
      return <OrchestrateAPIPage data-id="element-9" />;
    } else if (docsPath === '/docs/api-reference/payments') {
      return <PaymentsAPIPage data-id="element-10" />;
    } else if (docsPath === '/docs/api-reference/wallets') {
      return <WalletsAPIPage data-id="element-11" />;
    }
    // Default to docs home for unmatched docs routes
    return <DocsHomePage data-id="element-12" />;
  };
  const renderPage = () => {
    if (currentPage === 'docs') {
      return renderDocsPage();
    }
    switch (currentPage) {
      case 'products':
        return <ProductsPage mode={mode} data-id="element-13" />;
      case 'solutions':
        return <SolutionsPage mode={mode} data-id="element-14" />;
      case 'developers':
        return <DevelopersPage mode={mode} data-id="element-15" />;
      default:
        return <HomePage mode={mode} data-id="element-16" />;
    }
  };
  const showNavigation = currentPage !== 'docs';
  const showFooter = currentPage !== 'docs';
  return <div className="min-h-screen bg-white flex flex-col font-sans text-slate-900 antialiased selection:bg-blue-100 selection:text-blue-900" data-id="element-17">
      {showNavigation && <Navigation mode={mode} setMode={handleModeChange} data-id="element-18" />}

      {renderPage()}

      {showFooter && <Footer data-id="element-19" />}
    </div>;
}